import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Text, RadioGroup, Flex, Checkbox, Radio, Stack, Tabs, TabList, Tab, TabPanels, TabPanel } from '@chakra-ui/react';
import { NumericFormat } from 'react-number-format';
import { InputText } from '../Input';
import QuestionCard from './QuestionCard';
import { AttachmentButton } from '../Button';
import EvidenceViewerModal from '../Modals/EvidenceViewerModal';

const tabStyles = (isSelected) => ({
  color: isSelected ? '#292B30' : '#ADAFB3',
  bg: '#FEFEFE',
  minW: '123px',
  height: '46px',
  fontSize: '18px',
  fontWeight: 600,
  border: 'none',
  borderRadius: '8px 8px 0px 0px',
  alignItems: 'center',
  p: '14px 24px',
  _hover: { color: '#5C6066', bg: 'rgba(92, 96, 102, 0.1)' },
});

const QuestionsList = ({
  questions,
  register,
  control,
  surveyId,
  userId,
  companyId,
  isViewMode,
  defaultValues,
  handleModal
}) => {
  const surveyDefaultValues = defaultValues?.[surveyId] || {};

  return (
    <Stack spacing='4' mb={['20px', '40px']}>
      {questions?.map((question) => (
        <QuestionCard
          key={question?.id_question}
          question={question}
          surveyId={surveyId}
          userId={userId}
          companyId={companyId}
        >
          {question?.type === 'text' && (
            <InputText
              {...register(`${surveyId}.text_question_${question.id_question}.value`)}
              type='text'
              w='400px'
              expandable
              isDisabled={isViewMode}
              isViewMode={isViewMode}
              defaultValue={surveyDefaultValues[`text_question_${question.id_question}`]?.value || ''}
            />
          )}

          {question?.type === 'multiple_choice' && (
            <Flex gap='6' flexWrap='wrap'>
              {question?.options?.map((option, i) => (
                <Checkbox
                  key={option?.id_question_option_template}
                  value={option?.description}
                  id={`${surveyId}.checkbox_question_${question.id_question}_${option.id_question_option_template}_${i}`}
                  {...register(`${surveyId}.checkbox_question_${question.id_question}.value`)}
                  className='regular-checkbox'
                  isReadOnly={true}
                  defaultChecked={surveyDefaultValues[`checkbox_question_${question.id_question}`]?.value?.includes(option?.description)}
                >
                  <Text fontSize='sm' color='#5C6066'>
                    {option?.description}
                  </Text>
                </Checkbox>
              ))}
            </Flex>
          )}

          {question?.type === 'multi_choice' && (
            <RadioGroup
              defaultValue={surveyDefaultValues[`radio_question_${question.id_question}`]?.value || ''}
              isDisabled={isViewMode}
            >
              <Flex gap='12px' flexWrap='wrap' className='questionary-app'>
                {question?.options?.map((option, i) => (
                  <Radio
                    key={option.id_question_option_template}
                    id={`${surveyId}.radio_question_${question.id_question}_${option?.id_question_option_template}_${i}`}
                    {...register(`${surveyId}.radio_question_${question?.id_question}.value`)}
                    value={option.description}
                  >
                    {option?.description}
                  </Radio>
                ))}
              </Flex>
            </RadioGroup>
          )}

          {question?.type === 'number' && (
            <InputText
              {...register(`${surveyId}.number_question_${question.id_question}.value`)}
              type='number'
              w='400px'
              isDisabled={isViewMode}
              isViewMode={isViewMode}
              defaultValue={surveyDefaultValues[`number_question_${question.id_question}`]?.value || ''}
            />
          )}

          {question?.type === 'money' && (
            <Flex w='400px'>
              <Controller
                name={`${surveyId}.money_question_${question?.id_question}.value`}
                control={control}
                defaultValue={surveyDefaultValues[`money_question_${question.id_question}`]?.value || ''}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    displayType='input'
                    thousandSeparator=','
                    decimalSeparator='.'
                    prefix='R$ '
                    decimalScale={3}
                    getInputRef={ref}
                    min={0}
                    customInput={InputText}
                    isDisabled={isViewMode}
                    isViewMode={isViewMode}
                    {...rest}
                  />
                )}
              />
            </Flex>
          )}

          {question?.type === 'percentage' && (
            <Flex w='400px'>
              <Controller
                name={`${surveyId}.percentage_question_${question.id_question}.value`}
                control={control}
                defaultValue={surveyDefaultValues[`percentage_question_${question.id_question}`]?.value || ''}
                render={({ field: { ref, onChange, ...rest } }) => (
                  <NumericFormat
                    suffix='%'
                    displayType='input'
                    maxLength={4}
                    fixedDecimalScale
                    onValueValue={(values) => {
                      const { value } = values;
                      const numericValue = parseInt(value, 10);
                      if (isNaN(numericValue)) {
                        onChange(value);
                      } else if (numericValue <= 100) {
                        onChange(value);
                      } else {
                        onChange('100');
                      }
                    }}
                    getInputRef={ref}
                    customInput={InputText}
                    isDisabled={isViewMode}
                    isViewMode={isViewMode}
                    {...rest}
                  />
                )}
              />
            </Flex>
          )}

          <AttachmentButton
            surveyId={surveyId}
            control={control}
            question={question}
            handleModal={handleModal}
          />
        </QuestionCard>
      ))}
    </Stack>
  );
};

const CertificationSurveyReplica = ({
 surveyId,
 questions,
 defaultValues,
 userId,
 companyId,
 isViewMode = true,
 replicaData = [],
 isReplicable = false
}) => {
 const [attachEvidenceIsOpen, setAttachEvidenceIsOpen] = useState(false);
 const [currentQuestionName, setCurrentQuestionName] = useState();
 const [currentQuestion, setCurrentQuestion] = useState();
 const [selectedIndex, setSelectedIndex] = useState(0);

 const { register, control, handleSubmit } = useForm({
   defaultValues
 });

 const onSubmit = async (data) => {
  if (isViewMode) return;
 };

 const handleModal = (questionName, question) => {
  setCurrentQuestionName(questionName);
  setCurrentQuestion(question);
  setAttachEvidenceIsOpen(true);
 };

 const closeModal = () => {
  setAttachEvidenceIsOpen(false);
  setCurrentQuestionName(undefined);
  setCurrentQuestion(undefined);
 };

 return (
  <>
   {currentQuestionName && currentQuestion && (
    <EvidenceViewerModal
     isOpen={attachEvidenceIsOpen}
     onClose={closeModal}
     control={control}
     currentQuestionName={currentQuestionName}
    />
   )}

   <form onSubmit={handleSubmit(onSubmit)}>
     {isReplicable ? (
       <Tabs index={selectedIndex} onChange={setSelectedIndex} pos='static'>
         <TabList
           gap='8px'
           border='none'
           overflowX='auto'
           overflowY='hidden'
           css={{
             '&::-webkit-scrollbar': { display: 'none' },
             scrollbarWidth: 'none',
           }}
         >
           <Tab {...tabStyles(0 === selectedIndex)} borderTopRadius='8px'>
             Principal
           </Tab>
           {replicaData.map((_, index) => (
             <Tab key={index} {...tabStyles(index + 1 === selectedIndex)}>
               Replicado {index + 1}
             </Tab>
           ))}
         </TabList>

         <TabPanels bg='white'>
           <TabPanel pt='0'>
             <QuestionsList
               questions={questions}
               register={register}
               control={control}
               surveyId={surveyId}
               userId={userId}
               companyId={companyId}
               isViewMode={isViewMode}
               defaultValues={defaultValues}
               handleModal={handleModal}
             />
           </TabPanel>

           {replicaData.map((replica, index) => (
             <TabPanel key={index} pt='0'>
               <QuestionsList
                 questions={replica?.survey?.questions || []}
                 register={register}
                 control={control}
                 surveyId={replica?.survey?.id_survey}
                 userId={userId}
                 companyId={companyId}
                 isViewMode={isViewMode}
                 defaultValues={defaultValues}
                 handleModal={handleModal}
               />
             </TabPanel>
           ))}
         </TabPanels>
       </Tabs>
     ) : (
       <QuestionsList
         questions={questions}
         register={register}
         control={control}
         surveyId={surveyId}
         userId={userId}
         companyId={companyId}
         isViewMode={isViewMode}
         defaultValues={defaultValues}
         handleModal={handleModal}
       />
     )}
   </form>
  </>
 );
};

export default CertificationSurveyReplica;